import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter, matchPath, generatePath } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import styled from 'styled-components';
import memoize from 'transmute/memoize';
import partial from 'transmute/partial';
import { NavMarker } from 'react-rhumb';
import getComponentName from 'react-utils/getComponentName';
import UIFlex from 'UIComponents/layout/UIFlex';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { getIsCanvasVirtualRenderingDisabled } from 'automation-ui-canvas/selectors/platformFlowsApplicationSelectors';
import ActionConfigComponents from 'automation-ui-canvas/action-features/ActionConfigComponents';
import ActionConfigBasePaths from 'automation-ui-action-features/actions/ActionConfigBasePaths';
import { basePathToActionMetadataConfig } from 'automation-ui-action-features/actions/ActionMetadataConfigUtils';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { parseLocation } from 'automation-ui-routing/utils';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import * as Scopes from 'automation-ui-canvas/constants/Scopes';
import { getObjectTypeId, getActionOutputsForTargetActionById, getVersion } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { getFlowValidation, getFlowValidationObjectRequestStatus } from 'automation-ui-canvas/selectors/flowSelectors';
import { updateFlowLocally, requestFlowValidationObject } from 'automation-ui-canvas/actions/FlowActions';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import { atomicQueryUpdate } from 'automation-ui-routing/historyUtils';
import { getSourceApp } from 'automation-ui-canvas/selectors/embedSelectors';
import ReadOnlyBanner from 'automation-ui-canvas/components/common/ReadOnlyBanner';
import PannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { getHighlightedActionIds } from 'automation-ui-canvas/selectors/platformFlowsApplicationSelectors';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { queryAllExtensionDefinitions } from 'automation-ui-action-features/actions/extension/extensionQueries';
import { isGoToActionId } from 'automation-ui-action-features/records/recordsUtils';
import Action from 'automation-ui-action-features/records/Action';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import { wireActionOutputs } from 'automation-ui-action-features/queries/actionOutputQueries';
import { queryObjectsInActionsPolling } from 'automation-ui-canvas/action-features/objects-in-action/ObjectsInActionQueries';
import ConnectorButton from 'automation-ui-canvas/components/canvas/ConnectorButton';
import { wireFlowById, wireUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import { wireFlowIdFromParams } from 'automation-ui-canvas/containers/hocs/wireFlowIdFromParams';
import { EDIT_ACTIONS } from 'automation-ui-permissions/feature-permissions/constants/WorkflowManagementFeatures';
import { withFeaturePermissions } from 'automation-ui-permissions/feature-permissions/decorators/FeaturePermissionsDecorator';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import { ALL_ACTIONS_LIST, CANVAS_ROOT_ACTION_SET, PLATFORM_ACTION_EDIT } from 'automation-ui-canvas/constants/AbsolutePaths';
import { buildSearchStringWithCurrentSourceFields } from 'automation-ui-canvas/utils/routingUtils';
import withFlpAccess from 'automation-ui-permissions/flp/hocs/withFlpAccess';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowCanvasActionSetDecorator__StyledCanvas",
  componentId: "uleio7-0"
})(["height:100%;"]);
function getComposedCanvas() {
  return PannableDecorator(ZoomableDecorator({
    // TODO(TS_BUG)
    // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
    // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
    WrappedComponent: StyledCanvas,
    shouldRenderUndoRedoButtons: true
  }));
}
const MAX_ACTION_COUNT = 100;
export default function ActionSetCanvasDecorator(WrappedComponent, centerOnMount) {
  const componentName = getComponentName(WrappedComponent);
  class ActionSetCanvas extends Component {
    constructor(...args) {
      super(...args);
      this.state = {
        showLoadingOverlay: false
      };
      this.isNew = this.props.isNew;
      this.isEmbedded = false;
      this.ComposedCanvas = getComposedCanvas();
      this.toggleAddEmptyActionToFlow = (selectedEdgeId, forceRemove = false) => {
        const {
          flow,
          dispatch
        } = this.props;
        if (!flow) {
          return;
        }
        const emptyAction = new Action({
          actionType: ActionTypes.EMPTY_ACTION,
          actionId: SpecialActionIds.NEW_ACTION
        });
        let newFlow = flow;
        const addAction = !!selectedEdgeId;
        if (addAction && !forceRemove) {
          newFlow = newFlow.removeAction(SpecialActionIds.NEW_ACTION);
          newFlow = newFlow.insertAction({
            action: emptyAction,
            edgeId: selectedEdgeId,
            actionId: SpecialActionIds.NEW_ACTION
          });
        } else {
          newFlow = newFlow.removeAction(SpecialActionIds.NEW_ACTION);
        }
        dispatch(updateFlowLocally(newFlow));
      };
      this.getComposedProps = () => {
        const {
          flow,
          canvasScrollElementRef,
          highlightedActionIds,
          flowValidation,
          zoomLevel
        } = this.props;
        return {
          flow,
          canvasScrollElementRef,
          highlightedActionIds,
          flowValidation,
          // passed to force a refresh of the canvas
          onRenderCanvasNode: this.onRenderCanvasNode,
          onSelectAction: this.onSelectAction,
          TopContent: this.renderTopContent(),
          ComposedCanvas: this.ComposedCanvas,
          goToEditNodeId: null,
          onRenderConnectorButton: this.onRenderConnectorButton,
          onRenderEndMarker: this.onRenderEndMarker,
          // override required otherwise will render default
          showEnrollmentCard: false,
          centerOnMount,
          zoomLevel
        };
      };
      this.preloadActionConfigChunks = (props = this.props) => {
        const {
          flow
        } = props;
        if (!flow) {
          return;
        }
        flow.actions.map(action => action.actionType).toSet().forEach(actionType => {
          const LoadableConfig = ActionConfigComponents[actionType];
          if (LoadableConfig) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            LoadableConfig.preload();
          }
        });
      };
      this.shouldHideEmptyFlow = (props = this.props) => {
        const {
          flow
        } = props;
        // todo: figure this out
        if (!flow) {
          return false;
        }
        return false;
      };
      this.handleGetActionOutputsForTargetActionById = (targetActionId, isNew) => {
        return getActionOutputsForTargetActionById({
          flow: this.props.flow,
          allOutputsByActionTypeId: this.props.allOutputsByActionTypeId,
          instanceOutputsByActionId: this.props.instanceOutputsByActionId,
          targetActionId,
          isNewAction: isNew
        });
      };
      this.toggleSelectedEdgeId = (edgeId, remove) => {
        const {
          history,
          flowId: activeFlowId
        } = this.props;
        if (remove) {
          atomicQueryUpdate({}, ['edgeId', 'activeFlowId'], history, true);
        } else {
          // make sure to also remove the actionId
          atomicQueryUpdate({
            edgeId,
            activeFlowId
          }, ['actionId'], history, true);
        }
      };
      this.onSelectAction = (id, event = {}) => {
        const {
          tab
        } = event;
        const {
          flow,
          history,
          flowId
        } = this.props;
        let configRoute = '';
        if (id !== CanvasIdentifiers.FIRST_NODE) {
          const actionId = Number(id);
          const action = flow.getActionById(actionId);
          const actionType = action ? action.getActionType() : null;
          configRoute = actionType ? ActionConfigBasePaths[actionType] : undefined;
        }
        const pathname = generatePath(PLATFORM_ACTION_EDIT, {
          flowId,
          actionId: id,
          screen: 'action-set',
          configRoute
        });
        const search = new URLSearchParams(history.location.search);
        if (isGoToActionId(id)) {
          this.toggleSelectedEdgeId(null, true);
          history.replace({
            pathname: generatePath(CANVAS_ROOT_ACTION_SET, {
              flowId
            }),
            search: search.toString()
          });
          return;
        }
        this.toggleSelectedEdgeId(null, true);

        // TODO: what is tab here?

        if (tab) {
          // we can assume the edge will always be this since there are no other actions
          search.set('tab', tab);
        }
        history.replace({
          pathname,
          search: search.toString(),
          state: {
            doNotScrollTo: true
          }
        });
      };
      this.handleIsNew = (props = this.props) => {
        const {
          flow,
          edgeId
        } = props;
        if (this.isNew && flow && !flow.firstActionId && !edgeId // ensure the edge isn't selected when programatically clicking based on isNew
        ) {
          this.onClickAddControl(CanvasIdentifiers.FIRST_NODE_EDGE);
        }
      };
      this.onClickAddControl = edgeId => {
        const {
          edgeId: selectedEdgeId,
          flow,
          flowId,
          history,
          location
        } = this.props;
        const removeEdgeId = selectedEdgeId === edgeId;
        if (removeEdgeId) {
          history.replace({
            pathname: generatePath(CANVAS_ROOT_ACTION_SET, {
              flowId
            }),
            search: buildSearchStringWithCurrentSourceFields({
              location
            })
          });
          return;
        }
        const search = new URLSearchParams(history.location.search);
        search.set('edgeId', String(edgeId));
        trackEvent(Trackables.ADD_ACTION_CONFIG, {
          objectTypeId: getObjectTypeId(flow),
          flowId
        });
        history.replace({
          pathname: generatePath(ALL_ACTIONS_LIST, {
            flowId,
            screen: 'action-set'
          }),
          search: search.toString()
        });
      };
      this.onRenderEndMarker = () => {
        return null;
      };
      this.onRenderConnectorButton = ({
        edge,
        readOnly,
        showEdgeTooltips
      }) => {
        const {
          flowId,
          flow
        } = this.props;
        const hasExceededMaxActions = flow.actions.size >= MAX_ACTION_COUNT;
        const isDisabled = hasExceededMaxActions || readOnly;
        const tooltipMessage = hasExceededMaxActions ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.actionSetCanvas.exceededMaxActions",
          options: {
            flowName: flow.name
          }
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.common.readOnlyTooltip"
        });
        return /*#__PURE__*/_jsx(ConnectorButton, {
          edge: edge,
          isMulti: false,
          flowId: flowId,
          showEdgeTooltips: showEdgeTooltips,
          "data-test-id": `single-connector-${edge.getId()}`,
          onClick: partial(this.onClickAddControl, edge.getId()),
          disabled: isDisabled,
          tooltip: tooltipMessage,
          placement: "left",
          "aria-label": I18n.text('edit.actions.addAction')
        });
      };
      this.onRenderCanvasNode = memoize(({
        id: nodeId,
        width,
        height
      }) => {
        const {
          flow,
          selectedActionId,
          highlightedActionIds,
          isCanvasVirtualRenderingDisabled,
          selectedActionMetadataConfig,
          objectsInActions,
          getFeaturePermission,
          flpAccessData,
          flpAccessError,
          zoomLevel
        } = this.props;
        const isHighlighted = highlightedActionIds && highlightedActionIds.has(nodeId);
        const isSelected = selectedActionId === nodeId.toString();
        if (nodeId === CanvasIdentifiers.FIRST_NODE) {
          return null;
        }
        const hasFlpWriteAccess = !flpAccessError && (flpAccessData === null || flpAccessData === void 0 ? void 0 : flpAccessData.hasAccess(EDIT_ACTIONS));
        const {
          hasFeatureAccess: hasEditActionAccess
        } = getFeaturePermission({
          feature: EDIT_ACTIONS
        });
        return /*#__PURE__*/_jsx(FlowCanvasNode, {
          id: nodeId,
          width: width,
          height: height,
          flow: flow,
          isSelected: isSelected,
          isHighlighted: isHighlighted,
          readOnly: !hasEditActionAccess || !hasFlpWriteAccess,
          showIcon: true,
          isCanvasVirtualRenderingDisabled: isCanvasVirtualRenderingDisabled,
          objectsInActions: objectsInActions,
          setShowLoadingOverlay: loading => this.setState({
            showLoadingOverlay: loading
          }),
          onSelectNode: this.onSelectAction,
          selectedActionMetadataConfig: selectedActionMetadataConfig,
          getActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById,
          zoomLevel: zoomLevel
        });
      },
      /**
       * This advanced memo cache key ensures action
       * cards are memoized whenever possible. This
       * is a necessary optimization to make the
       * virtual renderer smooth for large workflows.
       */
      ({
        id: nodeId
      }) => {
        const {
          flowValidation,
          selectedActionId,
          highlightedActionIds,
          flowId,
          isCanvasVirtualRenderingDisabled,
          selectedActionMetadataConfig,
          flow,
          objectsInActions,
          isActionOutputsLoading,
          zoomLevel
        } = this.props;

        // Since updating actions updates the flow
        // version, this is a safe optimization to
        // avoid virtual actions triggering busting
        // this cache
        const flowVersion = flow.getVersion();
        const stagedFlowVersion = flow.stagedVersion;
        const isHighlighted = highlightedActionIds && highlightedActionIds.has(nodeId);
        const isSelected = selectedActionId === nodeId.toString();
        let selectedActionMetadataConfigForNewAction;
        if (nodeId === SpecialActionIds.NEW_ACTION) {
          selectedActionMetadataConfigForNewAction = selectedActionMetadataConfig;
        }
        const objectsInThisAction = objectsInActions ? objectsInActions.find(delayedAction => delayedAction.actionId === nodeId) : null;
        const objectsInThisActionCount = objectsInThisAction && objectsInThisAction.count;
        return `${nodeId}-${flowVersion}-${uniqueIdForObject(flowValidation)}-${isSelected}-${isHighlighted}-${flowId}-${isCanvasVirtualRenderingDisabled}-${selectedActionMetadataConfigForNewAction}-${objectsInThisActionCount}-actionOutputsLoading:${isActionOutputsLoading}-stagedFlowVersion:${stagedFlowVersion}-zoomLevel:${zoomLevel}`;
      });
    }
    componentDidMount() {
      const {
        edgeId,
        flowId,
        flow,
        history,
        location
      } = this.props;
      this._isMounted = true;

      // EJM - Redirect to add new action when we load a set with no actions.
      //Users can close the action list and still see the blank canvas
      if (flow && matchPath(location.pathname, {
        path: '/platform/flow/:flowId/action-set',
        exact: true,
        strict: false
      }) && !flow.actions.size) {
        const search = new URLSearchParams(location.search);
        // we can assume the edge will always be this since there are no other actions
        search.set('edgeId', 'enrollment-edge-0');
        history.replace({
          pathname: generatePath(ALL_ACTIONS_LIST, {
            flowId,
            screen: 'action-set'
          }),
          search: search.toString()
        });
      }
      this.handleIsNew();
      this.preloadActionConfigChunks();
      this.toggleAddEmptyActionToFlow(edgeId);
    }
    componentDidUpdate(prevProps) {
      const {
        flow,
        dispatch,
        edgeId: selectedEdgeId,
        history,
        location
      } = this.props;
      if (flow !== prevProps.flow) {
        this.preloadActionConfigChunks(this.props);
      }

      // This check looks like it was previously added to detect Flow changes
      // without unmounting. The new canvas algorithm modifies the Flow object
      // when there's a staged action being added. This causes it to appear to
      // change even though it hasn't.
      const justLoadedFlow = !prevProps.flow && flow;
      const isDifferentFlow = flow && prevProps.flow && prevProps.flow.flowId !== flow.flowId;
      if (justLoadedFlow || isDifferentFlow) {
        this.handleIsNew(this.props);
      }
      if (flow) {
        if (prevProps.flow && flow.getActions() !== prevProps.flow.getActions() && flow.getVersion() > prevProps.flow.getVersion()) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          dispatch(requestFlowValidationObject(flow));
        }
        // EJM - Redirect to add new action when we load a set with no actions.
        //Users can close the action list and still see the blank canvas
        if (!prevProps.flow && matchPath(location.pathname, {
          path: '/platform/flow/:flowId/action-set',
          exact: true,
          strict: false
        }) && !flow.actions.size) {
          const search = new URLSearchParams(location.search);
          search.set('edgeId', 'enrollment-edge-0');
          history.replace({
            pathname: generatePath(ALL_ACTIONS_LIST, {
              flowId: flow.flowId,
              screen: 'action-set'
            }),
            // we can assume the edge will always be this since there are no other actions
            search: search.toString()
          });
        }
      }

      //TODO: verify this logic, might just be for revisions and not needed
      if (selectedEdgeId !== prevProps.edgeId || getVersion(flow) !== getVersion(prevProps.flow)) {
        this.toggleAddEmptyActionToFlow(selectedEdgeId);
      }
    }
    componentWillUnmount() {
      this._isMounted = false;
    }
    renderTopContent() {
      return /*#__PURE__*/_jsx(_Fragment, {
        children: this.renderReadOnlyAlert()
      });
    }
    renderReadOnlyAlert() {
      const {
        flow
      } = this.props;
      return /*#__PURE__*/_jsx(UIFlex, {
        justify: "center",
        children: /*#__PURE__*/_jsx(ReadOnlyBanner, {
          flow: flow,
          editFeatures: [EDIT_ACTIONS]
        })
      });
    }
    render() {
      const {
        showLoadingOverlay
      } = this.state;
      const {
        flow
      } = this.props;

      // TODO: handle error fetching flow and add to Raegan
      if (!flow) {
        return null;
      }
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [showLoadingOverlay && /*#__PURE__*/_jsx(UILoadingOverlay, {
          use: "dots"
        }), /*#__PURE__*/_jsx(NavMarker, {
          name: "FLOW_CANVAS"
        }), /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, this.getComposedProps()))]
      });
    }
  }
  ActionSetCanvas.defaultProps = {
    readOnly: false,
    showEnrollmentCardInEmbed: false,
    canvasScrollElementRef: null
  };
  const ConnectedActionSetCanvas = compose(withFeaturePermissions, withRouter, wireAuthConsumer, wireFlowIdFromParams, wireFlowById, connect((state, {
    location,
    match,
    overrideParams,
    hasScope,
    flow,
    flowId
  }) => {
    const {
      actionId: selectedActionId = null,
      actionType: selectedActionTypePath = null
    } = overrideParams || match.params;
    const isEmbedded = false;
    const parsedQuery = parseLocation(location);
    const isNew = Boolean(parsedQuery.isNew);
    const workflowId = flow && flow.getWorkflowId();
    const highlightedActionIds = getHighlightedActionIds(state);
    const flowValidation = getFlowValidation(flowId)(state);
    const selectedActionMetadataConfig = selectedActionTypePath ? basePathToActionMetadataConfig(selectedActionTypePath, parsedQuery) : null;
    const hasWorkflowsAppAccess = hasScope(Scopes.WORKFLOWS_APP_ACCESS);
    return {
      flowValidationObjectRequestStatus: getFlowValidationObjectRequestStatus(state),
      flow,
      flowId,
      workflowId,
      flowValidation,
      sourceApp: getSourceApp(state),
      selectedActionId,
      showEnrollmentCardInEmbed: false,
      selectedActionMetadataConfig,
      embedId: null,
      edgeId: parsedQuery.edgeId,
      portalId: state.auth.portal.portal_id,
      isEmbedded,
      isNew,
      highlightedActionIds,
      hasWorkflowsAppAccess,
      showEdgeTooltips: true,
      isCanvasVirtualRenderingDisabled: getIsCanvasVirtualRenderingDisabled(state),
      showEnrollmentCard: false,
      // we should NEVER show the enrollment card!
      zoomLevel: getZoomLevel(flowId)(state)
    };
  }, dispatch => ({
    dispatch
  })), withFlpAccess, wireUpdateFlow, queryObjectsInActionsPolling,
  // Requires flow and flowId
  wireActionOutputs, queryAllExtensionDefinitions // Requires flowSourceApp and flowObjectTypeId
  )(ActionSetCanvas);
  ConnectedActionSetCanvas.displayName = `ConnectedActionSetCanvas(${componentName})`;
  attachWrappedComponent(ConnectedActionSetCanvas, WrappedComponent);
  return ConnectedActionSetCanvas;
}